import React, { useEffect } from "react";
import { Button, Grid, Typography } from "@material-ui/core";
import ErrorBoundary from "pages/dashboard/admin/import/interest_imports/components/boundary";
import FieldsNew from "pages/dashboard/admin/import/interest_imports/components/fields-new";
import { StepProps } from "..";
import { useStepData } from "../context";
import StepLayout from "./layout";
import { validateStep } from "./validation";

export const guessMapping = (label: string): string => {
  const normalized = label.trim().toLowerCase();

  if (normalized.includes("fname") || normalized.includes("first name")) {
    return "household_member_0_first_name";
  }

  if (normalized.includes("lname") || normalized.includes("last name")) {
    return "household_member_0_last_name";
  }

  if (normalized.includes("submit date") || normalized.includes("date")) {
    return "date";
  }
  if (normalized.includes("submit time") || normalized.includes("time")) {
    return "time";
  }
  if (normalized.includes("position")) {
    return "position_number";
  }

  if (normalized.includes("address 1")) {
    if (normalized.includes("mailing") || normalized.includes("mail")) {
      return "mailing_address1";
    }
    return "residence_address1";
  }

  if (normalized.includes("address 2")) {
    if (normalized.includes("mailing") || normalized.includes("mail")) {
      return "mailing_ste_number";
    }
    return "residence_ste_number";
  }

  if (normalized.includes("city")) {
    if (normalized.includes("mailing") || normalized.includes("mail")) {
      return "mailing_city";
    }
    return "residence_city";
  }

  if (normalized.includes("state")) {
    if (normalized.includes("mailing") || normalized.includes("mail")) {
      return "mailing_state";
    }
    return "residence_state";
  }

  if (normalized.includes("zip") || normalized.includes("postal")) {
    if (normalized.includes("mailing") || normalized.includes("mail")) {
      return "mailing_zip";
    }
    return "residence_zip";
  }

  if (normalized.includes("income")) {
    return "total_income";
  }

  if (normalized.includes("household count") ||
      normalized.includes("household size") ||
      normalized.includes("hh size") ||
      normalized.includes("hh count")) {
    return "household_count";
  }

  if (normalized.includes("unit #") || normalized.includes("unit number")) {
    return "movein_unit_no";
  }
  if (normalized.includes("studio")) {
    return "unit_size_studio";
  }
  if (normalized.includes("sro")) {
    return "unit_size_sro";
  }

  if ((normalized.includes("1bdr") ||
       normalized.includes("1br") ||
       normalized.includes("1 bdr") ||
       normalized.includes("1 br")) &&
      normalized.includes("den")) {
    return "unit_size_1br_den";
  }

  if (normalized.includes("1br") ||
      normalized.includes("1bdr") ||
      normalized.includes("1 br") ||
      normalized.includes("1 bdr")) {
    return "unit_size_1br";
  }

  if (normalized.includes("2br") ||
      normalized.includes("2 bdr") ||
      normalized.includes("2 br") ||
      normalized.includes("2bdr")) {
    return "unit_size_2br";
  }

  if (normalized.includes("3br") ||
      normalized.includes("3 bdr") ||
      normalized.includes("3 br") ||
      normalized.includes("3bdr")) {
    return "unit_size_3br";
  }

  if (normalized.includes("4br") ||
      normalized.includes("4 bdr") ||
      normalized.includes("4 br") ||
      normalized.includes("4bdr")) {
    return "unit_size_4br";
  }

  if (normalized.includes("5br") ||
      normalized.includes("5 bdr") ||
      normalized.includes("5 br") ||
      normalized.includes("5bdr")) {
    return "unit_size_5br";
  }

  if (normalized.includes("phone")) {
    if (normalized.includes("contact")) {
      return "contact_primary_phone";
    }
    if (normalized.includes("secondary")) {
      return "household_member_0_secondary_phone";
    }
    return "household_member_0_primary_phone";
  }

  if (normalized.includes("ext")) {
    if (normalized.includes("contact")) {
      return "contact_primary_phone_ext";
    }
    return "household_member_0_secondary_phone_ext";
  }

  if (normalized.includes("e-mail") || normalized.includes("email")) {
    if (normalized.includes("contact")) {
      return "contact_email";
    }
    return "household_member_0_email";
  }

  if (normalized.includes("voucher")) {
    return "household_has_housing_authority_voucher";
  }

  if (normalized === "removed?") {
    return "denied";
  }

  if (normalized === "moved in?") {
    return "moved_in";
  }

  return "none";
};

const StepMatch: React.FC<StepProps> = () => {
  const { stepData, setStepData, setStepName } = useStepData();
  const [highlightUnmatched, setHighlightUnmatched] = React.useState(false);

  if (!validateStep(stepData, "match")) {
    return null;
  }

  const applyAutoMatch = () => {
    // Collect already assigned fields from stepData.mapping
    const usedFields = new Set<string>(
      Object.values(stepData.mapping).map(mapping => mapping.field),
    );

    const newMapping = headers.reduce((acc, header) => {
      if (!stepData.mapping[header]) {
        let guessed = guessMapping(header);
        // If guessed field is already used, mark as "none"
        if (guessed !== "none" && usedFields.has(guessed)) {
          guessed = "none";
        }
        // If the guess is valid, add it to the used set
        if (guessed !== "none") {
          usedFields.add(guessed);
        }
        acc[header] = { field: guessed, format: "" };
      }
      return acc;
    }, {} as Record<string, { field: string, format: string }>);

    setStepData({
      ...stepData,
      mapping: {
        ...stepData.mapping,
        ...newMapping,
      },
    });

    setHighlightUnmatched(true);
  };


  const allMappedCount = Object.keys(stepData.mapping).length;
  // Excluding Deleted and Passthrough
  const mappedCount = Object.keys(stepData.mapping).filter(
    key => ["none", "passthrough"].includes(stepData.mapping[key].field),
  ).length;

  const candidate = stepData.data[0];

  const headers = Object.keys(candidate);

  useEffect(() => {
    setStepName("Match");
  }, []);

  return <StepLayout title="Match spreadsheet columns to Haven Connect fields"
    description={<>
      <Typography variant="body1">
        Match spreadsheet columns to application fields in Haven Connect to ensure all key information is imported.
      </Typography>
      <ul>
        <li><Typography variant="body1">Columns marked as <strong>Skip</strong> will not be imported.</Typography></li>
        <li><Typography variant="body1">Columns marked as <strong>Add to Notes</strong> will be imported as notes.</Typography></li>
        <li><Typography variant="body1">Applications will be imported as <strong>Active</strong> unless marked as <strong>Removed</strong> or <strong>Moved In</strong>.</Typography></li>
        <li><Typography variant="body1">At least 3 columns must be mapped to import applications.</Typography></li>
      </ul>
    </>}>
    <Grid item xs={12}>
      <Button onClick={applyAutoMatch} variant="contained" color="primary">
        Auto-match columns
      </Button>
      <Typography variant="body1">
        If we can't match a column for you, we'll highlight it in red.
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <ErrorBoundary message="An unexpected error was encountered while displaying the contents of the CSV.">
        <FieldsNew
          state={stepData}
          onSetMapping={(from, to) =>
            setStepData({
              ...stepData,
              mapping: { ...stepData.mapping, [from]: { field: to, format: "" } },
            })
          }
          highlightUnmatched={highlightUnmatched}
        />
      </ErrorBoundary>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="body1">
        {stepData.data.length} applications were recognized in this file
      </Typography>
      <Typography variant="body1">
        {allMappedCount - mappedCount} columns will be imported. {headers.length - (allMappedCount - mappedCount)} columns will not be imported.
      </Typography>
      {(allMappedCount - mappedCount) < 3 && (
        <Typography variant="body1" color="error">
          You must map at least 3 columns to import applications.
        </Typography>
      )}
    </Grid>
  </StepLayout>;
};

export default StepMatch;
